import { Injectable, computed, signal } from '@angular/core';

/** Un paso del recorrido: a qué elemento apunta y qué dice de él. */
export interface DxcTourStep {
    /** Selector del elemento que se señala. Si no está en la página o no se ve, el paso se salta. */
    target: string;
    title: string;
    body: string;
}

/**
 * Novedades de una vista: la portada que las resume y el recorrido que señala
 * dónde quedó cada cosa. Lo registra la vista que cambió, mientras está abierta.
 */
export interface DxcTour {
    /** Lleva la versión: un rediseño nuevo de la misma vista es otro id y se vuelve a ofrecer. */
    id: string;
    eyebrow: string;
    title: string;
    lead: string;
    bullets: string[];
    steps: DxcTourStep[];
    /** Escena animada de la portada. Sin ella, la portada lleva la marca. */
    scene?: 'load-details';
}

type DxcTourMode = 'intro' | 'steps' | null;

/**
 * Estado del recorrido de novedades. Una vista registra su recorrido al abrir
 * y lo retira al salir; la barra superior muestra el botón de ayuda mientras
 * haya uno, y `<dxc-tour-host>` (montado una vez, junto a los diálogos) lo pinta.
 *
 * «Ya visto» va en el almacenamiento del navegador, por usuario y por id. No va
 * en `User.settings`: la edición del perfil reemplaza ese objeto entero, así
 * que escribir una marca suelta borraría el resto. Si el navegador no deja
 * guardar, el peor caso es volver a ofrecer la portada.
 */
@Injectable({ providedIn: 'root' })
export class DxcTourService {

    readonly tour = signal<DxcTour | null>(null);
    readonly mode = signal<DxcTourMode>(null);
    readonly index = signal(0);

    /** Solo los pasos cuyo elemento existe ahora: un rol o un estado sin esa sección no lo ve. */
    readonly steps = signal<DxcTourStep[]>([]);
    readonly step = computed(() => this.steps()[this.index()] ?? null);

    #offerTimer: ReturnType<typeof setTimeout> | null = null;

    register(tour: DxcTour): void {
        this.tour.set(tour);
    }

    unregister(id: string): void {
        if (this.tour()?.id !== id) return;
        this.#clearOffer();
        this.mode.set(null);
        this.tour.set(null);
    }

    /** Ofrece la portada una vez por usuario; espera a que la vista termine de pintarse. */
    offerOnce(id: string, delay = 900): void {
        if (this.tour()?.id !== id || this.hasSeen(id)) return;
        this.#clearOffer();
        this.#offerTimer = setTimeout(() => {
            if (this.tour()?.id === id && !this.mode()) this.openIntro();
        }, delay);
    }

    openIntro(): void {
        if (!this.tour()) return;
        this.mode.set('intro');
    }

    /** Los pasos que se pueden señalar ahora: su objetivo existe y se ve. Uno
     *  que solo existe a cierto ancho (oculto con `display:none`) no cuenta. */
    availableSteps(): DxcTourStep[] {
        return (this.tour()?.steps ?? []).filter(step => {
            const target = document.querySelector(step.target);
            return !!target && target.getClientRects().length > 0;
        });
    }

    start(): void {
        const tour = this.tour();
        if (!tour) return;
        const steps = this.availableSteps();
        if (!steps.length) return this.close();
        this.steps.set(steps);
        this.index.set(0);
        this.mode.set('steps');
    }

    next(): void {
        if (this.index() >= this.steps().length - 1) return this.close();
        this.index.update(i => i + 1);
    }

    prev(): void {
        this.index.update(i => Math.max(0, i - 1));
    }

    /** Cerrar en cualquier punto —«Ahora no», Esc, terminar— cuenta como visto. */
    close(): void {
        const tour = this.tour();
        if (tour) this.markSeen(tour.id);
        this.mode.set(null);
    }

    hasSeen(id: string): boolean {
        try {
            return localStorage.getItem(this.#key(id)) === '1';
        } catch {
            return false;
        }
    }

    markSeen(id: string): void {
        try {
            localStorage.setItem(this.#key(id), '1');
        } catch {
            // Sin almacenamiento: se volverá a ofrecer, nada más.
        }
    }

    #key(id: string): string {
        let user = '';
        try { user = localStorage.getItem('userId') ?? ''; } catch { /* sin almacenamiento */ }
        return `dxc-tour:${id}:${user}`;
    }

    #clearOffer(): void {
        if (this.#offerTimer) clearTimeout(this.#offerTimer);
        this.#offerTimer = null;
    }
}
